import isEmpty from 'lodash/isEmpty';

import { ASK_AICA_CONSTANTS, DEFAULT_SUGGESTED_QUESTIONS } from './constants';
import { LibraryFile, Message, ReasoningStep, SuggestedQuestion } from './actions';

interface AskAicaState {
    messages: Message[];
    reasoning?: ReasoningStep;
    isLoading: boolean; //this is used for api response when a question is asked.
    isLoadingChatHistory: boolean;
    isClearingChat: boolean;
    error: string | null;
    suggestedQuestions: SuggestedQuestion[];
    activeOrgId: string | null;
    pagination: {
        pageSize: number;
        page: number;
        hasMoreData: boolean;
    };
    uiState: {
        isOpen: boolean;
        selectedSources: string[];
        rightPanelMode: 'suggestions' | 'reasoning';
    };
    library: {
        files: LibraryFile[];
        isLoading: boolean;
    };
}

const initialState: AskAicaState = {
    messages: [],
    isLoading: false,
    isLoadingChatHistory: true,
    isClearingChat: false,
    error: null,
    suggestedQuestions: DEFAULT_SUGGESTED_QUESTIONS,
    activeOrgId: null,
    pagination: {
        pageSize: 5,
        page: 1,
        hasMoreData: true,
    },
    uiState: {
        isOpen: false,
        selectedSources: ['searchTheWeb', 'trustedPublicRecords', 'consentData', 'dataRoom'],
        rightPanelMode: 'suggestions',
    },
    library: {
        files: [],
        isLoading: false,
    },
};

const addMessage = (state: AskAicaState, action: any) => {
    return {
        ...state,
        // messages: [...state.messages, action.payload],
        messages: [action.payload, ...state.messages],
    };
};

const initMessageThread = (state: AskAicaState, action: any) => {
    return {
        ...state,
        messages: [...state.messages, ...action.payload],
        isLoadingChatHistory: false,
        pagination: {
            ...state.pagination,
            page: state.pagination.page + 1,
            hasMoreData: !!action.payload.length,
        },
    };
};

const updateMessage = (state: AskAicaState, action: any) => {
    const { id, content, metadata } = action.payload;
    return {
        ...state,
        messages: state.messages.map((message) =>
            message.id === id
                ? {
                      ...message,
                      // Replace with server-accumulated content when available;
                      // keep existing content when content is empty (e.g. metadata-only updates on completion).
                      content: content || message.content,
                      // Shallow merge: metadata.sources from the final chunk REPLACES any prior value
                      // (Athena emits sources only on the is_final chunk, so prior chunks have no sources).
                      metadata: { ...message.metadata, ...metadata },
                      // Promote feedback_ack from metadata to the top-level flag so both
                      // live (metadata.feedback_ack) and reload (isFeedbackAck set by history
                      // map) paths use the same field in MessagesContainer (A-14 / A-17).
                      isFeedbackAck: message.isFeedbackAck || !!metadata?.feedback_ack,
                  }
                : message,
        ),
    };
};

const clearMessages = (state: AskAicaState, action: any) => {
    return {
        ...state,
        messages: [],
        pagination: {
            ...initialState.pagination,
        },
        suggestedQuestions: DEFAULT_SUGGESTED_QUESTIONS,
        activeOrgId: action.payload ?? null,
        library: {
            ...initialState.library,
        },
        reasoning: undefined,
        uiState: {
            ...state.uiState,
            rightPanelMode: 'suggestions' as const,
        },
    };
};

const setIsLoading = (state: AskAicaState, action: any) => {
    return {
        ...state,
        isLoading: action.payload,
    };
};

const setIsLoadingChatHistory = (state: AskAicaState, action: any) => {
    return {
        ...state,
        isLoadingChatHistory: action.payload,
    };
};

const setIsClearingChat = (state: AskAicaState, action: any) => {
    return {
        ...state,
        isClearingChat: action.payload,
    };
};

const setError = (state: AskAicaState, action: any) => {
    return {
        ...state,
        error: action.payload,
    };
};

const setReasoning = (state: AskAicaState, action: any) => {
    const { reasoning, hasNewReasoning } = action.payload;
    if (isEmpty(reasoning) || !hasNewReasoning) return state;
    const latestReasoning = reasoning[reasoning.length - 1];
    return {
        ...state,
        reasoning: state.reasoning ? { ...state.reasoning, message: state.reasoning.message + latestReasoning.message } : latestReasoning,
    };
};

const resetReasoning = (state: AskAicaState) => {
    return {
        ...state,
        reasoning: undefined,
    };
};

// UI-related reducer functions
const setAskAicaOpen = (state: AskAicaState, action: any) => {
    return {
        ...state,
        uiState: {
            ...state.uiState,
            isOpen: action.payload,
        },
    };
};

const setAskAicaSelectedSources = (state: AskAicaState, action: any) => {
    return {
        ...state,
        uiState: {
            ...state.uiState,
            selectedSources: action.payload,
        },
    };
};

const setAskAicaRightPanelMode = (state: AskAicaState, action: any) => {
    return {
        ...state,
        uiState: {
            ...state.uiState,
            rightPanelMode: action.payload,
        },
    };
};

const setGlobalSuggestedQuestions = (state: AskAicaState, action: any) => {
    return {
        ...state,
        suggestedQuestions: action.payload,
    };
};

const updateGlobalSuggestedQuestions = (state: AskAicaState, action: any) => {
    return {
        ...state,
        suggestedQuestions: [...state.suggestedQuestions, ...action.payload],
    };
};

const setLibraryLoading = (state: AskAicaState, action: any) => {
    return {
        ...state,
        library: {
            ...state.library,
            isLoading: action.payload,
        },
    };
};

const setLibraryFiles = (state: AskAicaState, action: any) => {
    return {
        ...state,
        library: {
            ...state.library,
            files: action.payload,
            isLoading: false,
        },
    };
};

const addLibraryFiles = (state: AskAicaState, action: any) => {
    // Null-safe: a malformed/empty dispatch must not crash the .filter below.
    const newFiles: LibraryFile[] = action.payload ?? [];
    const existingKeys = new Set(state.library.files.map((file) => file.mnemosyneDocId || file.fileUrl));
    const dedupedNewFiles = newFiles.filter((file) => !existingKeys.has(file.mnemosyneDocId || file.fileUrl));
    if (dedupedNewFiles.length === 0) return state;

    return {
        ...state,
        library: {
            ...state.library,
            files: [...dedupedNewFiles, ...state.library.files],
        },
    };
};

const askAicaReducer = (state: AskAicaState = initialState, action: any): AskAicaState => {
    switch (action.type) {
        // Chat functionality actions
        case ASK_AICA_CONSTANTS.INITIALISE_MESSAGE_THREAD:
            return initMessageThread(state, action);
        case ASK_AICA_CONSTANTS.ADD_MESSAGE:
            return addMessage(state, action);
        case ASK_AICA_CONSTANTS.UPDATE_MESSAGE:
            return updateMessage(state, action);
        case ASK_AICA_CONSTANTS.CLEAR_MESSAGES:
            return clearMessages(state, action);
        case ASK_AICA_CONSTANTS.SET_IS_LOADING:
            return setIsLoading(state, action);
        case ASK_AICA_CONSTANTS.SET_IS_LOADING_CHAT_HISTORY:
            return setIsLoadingChatHistory(state, action);
        case ASK_AICA_CONSTANTS.SET_IS_CLEARING_CHAT:
            return setIsClearingChat(state, action);
        case ASK_AICA_CONSTANTS.SET_ERROR:
            return setError(state, action);
        case ASK_AICA_CONSTANTS.SET_REASONING:
            return setReasoning(state, action);
        case ASK_AICA_CONSTANTS.RESET_REASONING:
            return resetReasoning(state);

        // UI-related actions
        case ASK_AICA_CONSTANTS.SET_ASK_AICA_OPEN:
            return setAskAicaOpen(state, action);
        case ASK_AICA_CONSTANTS.SET_ASK_AICA_SELECTED_SOURCES:
            return setAskAicaSelectedSources(state, action);
        case ASK_AICA_CONSTANTS.SET_ASK_AICA_RIGHT_PANEL_MODE:
            return setAskAicaRightPanelMode(state, action);
        case ASK_AICA_CONSTANTS.SET_GLOBAL_SUGGESTED_QUESTIONS:
            return setGlobalSuggestedQuestions(state, action);
        case ASK_AICA_CONSTANTS.UPDATE_GLOBAL_SUGGESTED_QUESTIONS:
            return updateGlobalSuggestedQuestions(state, action);

        // Library-related actions
        case ASK_AICA_CONSTANTS.SET_LIBRARY_LOADING:
            return setLibraryLoading(state, action);
        case ASK_AICA_CONSTANTS.SET_LIBRARY_FILES:
            return setLibraryFiles(state, action);
        case ASK_AICA_CONSTANTS.ADD_LIBRARY_FILES:
            return addLibraryFiles(state, action);
        default:
            return state;
    }
};

export default askAicaReducer;
